import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/joy';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import type { ReactNode } from 'react';
import { KanbanCardShell } from './KanbanCard';
import { KanbanColumn } from './KanbanColumn';
import { resolveDropTarget } from './resolveDropTarget';
import { useKanbanScroll } from './useKanbanScroll';
import { COLUMN_GAP, COLUMN_WIDTH } from './types';
import type { KanbanColumnModel, KanbanDropZone, KanbanMoveArgs } from './types';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface KanbanBoardViewProps<TCard> {
  /** Namespaces droppable ids so two boards can be mounted at once. */
  boardScope: string;
  columns: KanbanColumnModel[];
  cards: TCard[];
  getCardId: (card: TCard) => UniqueIdentifier;
  getColumnId: (card: TCard) => number | null;
  getPosition?: (card: TCard) => number;
  renderCard: (card: TCard, ctx: { isOverlay: boolean }) => ReactNode;
  getCardActions?: (card: TCard) => ReactNode;
  getCardAccent?: (card: TCard) => string | undefined;
  onCardClick?: (card: TCard) => void;
  renderRow?: (card: TCard) => ReactNode;
  renderSummary?: (columnId: number, cards: TCard[]) => ReactNode;
  onMove: (args: KanbanMoveArgs<TCard>) => Promise<void>;
  /** Called when a move or drop-zone action rejects. Without it a failed move
   *  is an unhandled rejection and the card silently springs back. */
  onMoveError?: (error: unknown, args: KanbanMoveArgs<TCard>) => void;
  dropZones?: KanbanDropZone<TCard>[];
  onAddCard?: (columnId: number) => void;
  boardColor?: string;
  columnWidth?: number;
  dragDisabled?: boolean;
  viewMode?: 'board' | 'list';
  onViewModeChange?: (mode: 'board' | 'list') => void;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  emptyColumnsSlot?: ReactNode;
  toolbar?: ReactNode;
  /**
   * The board needs a definite height: its scroll container is absolutely
   * positioned, so `height: 100%` against an `auto` parent collapses to zero
   * and renders blank. The default matches the admin hub's panel height.
   */
  height?: string | number;
  emptyLabel?: string;
}

export function KanbanBoardView<TCard>({
  boardScope,
  columns,
  cards,
  getCardId,
  getColumnId,
  getPosition,
  renderCard,
  getCardActions,
  getCardAccent,
  onCardClick,
  renderRow,
  renderSummary,
  onMove,
  onMoveError,
  dropZones,
  onAddCard,
  boardColor,
  columnWidth = COLUMN_WIDTH,
  dragDisabled,
  viewMode,
  onViewModeChange,
  isLoading,
  isFetching,
  error,
  onRetry,
  emptyColumnsSlot,
  toolbar,
  height = 'calc(100vh - 340px)',
  emptyLabel,
}: KanbanBoardViewProps<TCard>) {
  const isMobile = useIsMobile();
  const [internalMode, setInternalMode] = useState<'board' | 'list'>(isMobile ? 'list' : 'board');
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);

  // Dragging stays off below the mobile breakpoint whatever the caller asks
  // for, and the list view is forced there. Letting `?view=board` win while the
  // sensors are empty strands the user on a board they cannot use.
  const noDrag = dragDisabled ?? isMobile;
  const mode = viewMode ?? internalMode;
  const effectiveMode = isMobile ? 'list' : mode;

  // The toggle is hidden on mobile because the list view is forced there, so
  // offering a switch that does nothing would just be a dead control.
  const showViewToggle = !isMobile;
  const toggleViewMode = () => {
    const next = mode === 'board' ? 'list' : 'board';
    // Controlled when the caller passes viewMode; otherwise we own the state.
    if (viewMode === undefined) setInternalMode(next);
    onViewModeChange?.(next);
  };

  const { setScrollNode } = useKanbanScroll({ step: columnWidth + COLUMN_GAP });

  // Hook order must not depend on `noDrag`, so the sensor is always created and
  // the list it goes into is what varies.
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const sensors = useSensors(...(noDrag ? [] : [pointerSensor]));

  const sortedColumns = useMemo(
    () => [...columns].sort((a, b) => a.order - b.order),
    [columns]
  );

  const cardsByColumn = useMemo(() => {
    const map = new Map<number, TCard[]>();
    // Seed every column so an empty one still has a bucket to drop into.
    sortedColumns.forEach((column) => map.set(column.id, []));
    cards.forEach((card) => {
      const key = getColumnId(card);
      if (key != null) map.get(key)?.push(card);
    });
    if (getPosition) {
      map.forEach((bucket) => bucket.sort((a, b) => getPosition(a) - getPosition(b)));
    }
    return map;
  }, [cards, sortedColumns, getColumnId, getPosition]);

  const activeCard = useMemo(
    () => (activeId == null ? null : cards.find((card) => getCardId(card) === activeId) ?? null),
    [activeId, cards, getCardId]
  );

  const handleDragStart = (event: DragStartEvent) => setActiveId(event.active.id);

  const handleDragOver = (event: DragOverEvent) => {
    const target = resolveDropTarget({
      over: event.over,
      activeId: event.active.id,
      cardsByColumn,
      getCardId,
    });
    setHoveredColumn(target?.kind === 'column' ? target.toColumnId : null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setHoveredColumn(null);

    const card = cards.find((c) => getCardId(c) === event.active.id);
    if (!card) return;

    const target = resolveDropTarget({
      over: event.over,
      activeId: event.active.id,
      cardsByColumn,
      getCardId,
    });
    if (!target) return;

    const fromColumnId = getColumnId(card);

    if (target.kind === 'zone') {
      const zone = dropZones?.find((z) => z.id === target.zoneId);
      try {
        await zone?.onDrop(card);
      } catch (error) {
        // An unhandled rejection here would leave the card snapped back with
        // no explanation of why.
        onMoveError?.(error, { card, fromColumnId, toColumnId: -1, toIndex: 0 });
      }
      return;
    }

    const samePlace =
      fromColumnId === target.toColumnId &&
      (cardsByColumn.get(target.toColumnId) ?? []).indexOf(card) === target.toIndex;
    if (samePlace) return;

    const args = { card, fromColumnId, toColumnId: target.toColumnId, toIndex: target.toIndex };
    try {
      await onMove(args);
    } catch (error) {
      onMoveError?.(error, args);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        color="danger"
        variant="soft"
        endDecorator={
          onRetry && (
            <Button size="sm" variant="soft" color="danger" onClick={onRetry}>
              Retry
            </Button>
          )
        }
      >
        {error.message || 'This board could not be loaded.'}
      </Alert>
    );
  }

  if (sortedColumns.length === 0) {
    return <>{emptyColumnsSlot ?? <Typography level="body-sm">This board has no columns yet.</Typography>}</>;
  }

  const listView = (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {sortedColumns.map((column) => {
        const bucket = cardsByColumn.get(column.id) ?? [];
        return (
          <Box key={column.id}>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              {column.name} ({bucket.length})
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {bucket.map((card) => (
                <Box key={String(getCardId(card))} onClick={() => onCardClick?.(card)}
                  role={onCardClick ? 'button' : undefined} tabIndex={onCardClick ? 0 : undefined}
                  onKeyDown={event=>{if(event.target===event.currentTarget && (event.key==='Enter'||event.key===' ')){event.preventDefault();onCardClick?.(card);}}}
                  sx={renderRow ? undefined : {p:2,border:'1px solid',borderColor:'divider',borderRadius:'lg',bgcolor:'background.surface',cursor:onCardClick?'pointer':undefined,'&:hover':{borderColor:'primary.400'},'&:focus-visible':{outline:'2px solid',outlineColor:'primary.500'}}}>
                  {renderRow ? renderRow(card) : renderCard(card, { isOverlay: false })}
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const board = (
    <Box sx={{ position: 'relative', height, minHeight: 540, display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={setScrollNode}
        tabIndex={0}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollBehavior: 'auto',
          WebkitOverflowScrolling: 'touch',
          contain: 'layout style paint',
          backfaceVisibility: 'hidden',
          outline: 'none',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 12 },
          '&::-webkit-scrollbar-track': { bgcolor: 'neutral.100', borderRadius: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'neutral.300', borderRadius: 6 },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'neutral.400' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: `${COLUMN_GAP}px`,
            p: 2,
            minHeight: 0,
            height: '100%',
            alignItems: 'stretch',
            width: 'max-content',
            // Trailing space so the last column can be scrolled clear of the edge.
            '&::after': { content: '""', flex: '0 0 50px', minWidth: 50 },
          }}
        >
          {sortedColumns.map((column) => (
            <KanbanColumn<TCard>
              key={column.id}
              boardScope={boardScope}
              column={column}
              cards={cardsByColumn.get(column.id) ?? []}
              getCardId={getCardId}
              renderCard={renderCard}
              getCardActions={getCardActions}
              getCardAccent={getCardAccent}
              onCardClick={onCardClick}
              boardColor={boardColor}
              isDropTarget={hoveredColumn === column.id}
              renderSummary={renderSummary}
              onAddCard={onAddCard}
              columnWidth={columnWidth}
              emptyLabel={emptyLabel}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {toolbar}
      {/* Kept out of the loading branch so a background refresh does not tear
          the board down and lose scroll position. */}
      {isFetching && <LinearProgress size="sm" />}

      {effectiveMode === 'list' ? (
        listView
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setHoveredColumn(null);
          }}
        >
          {board}

          <DragOverlay>
            {activeCard && (
              <KanbanCardShell
                id={getCardId(activeCard)}
                boardScope={boardScope}
                columnId={getColumnId(activeCard) ?? 0}
                isOverlay
                accentColor={getCardAccent?.(activeCard)}
              >
                {renderCard(activeCard, { isOverlay: true })}
              </KanbanCardShell>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {showViewToggle && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="plain" color="neutral" onClick={toggleViewMode}>
            {mode === 'board' ? 'List view' : 'Board view'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default KanbanBoardView;
